import { Key } from 'constants/keyCodes';
import { UserContextProjectMetadata } from 'core/userContext';
import { useEffect, useState } from 'react';
import { splitArray } from 'utils/arrayHelpers';
import { MenuOption } from '../MenuOption';
import { ProjectOption } from '../ProjectOption';
import { StyledDivider } from './AccountsAndProjectsMenu.styles';

export type ProjectsProps = {
  currentProjectId?: number;
  isPinnedProjectsEnabled: boolean;
  onAddAction: (id: number) => void;
  onRemoveAction: (id: number) => void;
  onChangeAction: (id: number) => void;
  projects: UserContextProjectMetadata[];
  searchTerm: string;
};

export const Projects = ({
  currentProjectId,
  isPinnedProjectsEnabled,
  onAddAction,
  onRemoveAction,
  onChangeAction,
  projects = [],
  searchTerm,
}: ProjectsProps) => {
  const [pinned, unpinned] = splitArray(projects, (project) =>
    Boolean(project?.isPinned && isPinnedProjectsEnabled),
  );
  const projectsList = isPinnedProjectsEnabled ? unpinned : projects;
  const shouldShowDivider = Boolean(
    isPinnedProjectsEnabled && pinned.length > 0 && projectsList.length > 0,
  );

  const currentProjects = [...pinned, ...unpinned];
  const [currentSelectedId, setCurrentSelectedId] = useState(currentProjects[0]?.id);

  const handleKeydown = (e: KeyboardEvent) => {
    const currentIndex = currentProjects.findIndex((p) => p.id === currentSelectedId);
    const isLastItem = currentIndex === currentProjects.length - 1;
    const isFirstItem = currentIndex === 0;
    const nextIndex = isLastItem ? 0 : currentIndex + 1;
    const previousIndex = isFirstItem ? currentProjects.length - 1 : currentIndex - 1;

    switch (e.key) {
      case Key.TAB: {
        e.preventDefault();
        setCurrentSelectedId(
          e.shiftKey ? currentProjects[previousIndex]?.id : currentProjects[nextIndex]?.id,
        );
        break;
      }
      case Key.ARROW_DOWN: {
        e.preventDefault();
        setCurrentSelectedId(currentProjects[nextIndex]?.id);
        break;
      }
      case Key.ARROW_UP: {
        e.preventDefault();
        setCurrentSelectedId(currentProjects[previousIndex]?.id);
        break;
      }
      case Key.ENTER: {
        e.preventDefault();
        onChangeAction(currentSelectedId);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    setCurrentSelectedId(currentProjects[0]?.id || -1);
  }, [searchTerm]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [currentSelectedId]);

  return (
    <>
      {pinned.map(({ id, isPinned, name }) => {
        return (
          <ProjectOption
            key={id}
            id={id}
            isCurrentPage={id === currentProjectId}
            isPinned={isPinned}
            isSelected={id === currentSelectedId}
            title={name}
            onActionClick={() => onRemoveAction(id)}
            onProjectClick={() => onChangeAction(id)}
          />
        );
      })}

      {/* if pinned projects are enabled, and both groups have projets: show a divider between the two */}
      {shouldShowDivider && <StyledDivider />}

      {unpinned.map(({ id, isPinned, name }) => {
        return isPinnedProjectsEnabled ? (
          <ProjectOption
            key={id}
            id={id}
            isCurrentPage={id === currentProjectId}
            isPinned={isPinned}
            isSelected={id === currentSelectedId}
            title={name}
            onActionClick={() => onAddAction(id)}
            onProjectClick={() => onChangeAction(id)}
          />
        ) : (
          <MenuOption
            key={id}
            isCurrentPage={id === currentProjectId}
            title={name}
            onClick={() => onChangeAction(id)}
          />
        );
      })}
    </>
  );
};
